import { useMemo, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { MetricGridView } from '@/components/views/MetricGridView';
import type { AppData } from '@/components/apps/useAppData';
import { recordTitle } from '@/components/apps/app-records-model';
import { Button } from '@/components/ui/Button';
import { outreachAction } from '@/lib/outreach';
import { cn } from '@/lib/utils';
import { outreachParameters } from '@shared/outreach';
import { CampaignAddDrawer } from './CampaignAddDrawer';
import { campaignCounts } from './campaign-counts';
import { ImportMenu } from './ImportMenu';
import { OutreachProspects } from './OutreachProspects';
import { OutreachReview } from './OutreachReview';
import { OutreachSetup } from './OutreachSetup';
import { type ImportSource, TableImportDrawer } from './TableImportDrawer';
import type { OutreachData } from './useOutreach';

type Tab = 'prospects' | 'steps' | 'settings';
const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'prospects', label: 'Prospects' },
  { key: 'steps', label: 'E-mails' },
  { key: 'settings', label: 'Réglages' },
];

interface OutreachCampaignViewProps {
  appId: string;
  app: AppData;
  data: OutreachData;
  canManage: boolean;
  onBack: () => void;
  onOpen: (sequenceId: string | null) => void;
}

/**
 * One campaign (04/11/2026), lemlist's campaign inside the app: its prospects
 * (objects of the list it holds), its e-mails, its settings, what came back.
 * Only its sender changes it; whoever sees the app reads it.
 */
export const OutreachCampaignView = ({ appId, app, data, canManage, onBack, onOpen }: OutreachCampaignViewProps) => {
  const campaign = data.campaign!;
  const [tab, setTab] = useState<Tab>('prospects');
  const [reviewing, setReviewing] = useState(false);
  const [importing, setImporting] = useState<ImportSource | null>(null);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const enrollments = data.inCampaign;
  const toReview = data.isOwner ? enrollments.filter((enrollment) => enrollment.status === 'review') : [];
  const writing = enrollments.filter((enrollment) => enrollment.status === 'drafting').length;
  const counts = campaignCounts(enrollments);

  const band = useMemo(() => ({
    presentation: 'band' as const,
    metrics: [
      { label: 'Contactés', value: String(counts.contacted) },
      { label: 'En cours', value: String(counts.active) },
      { label: 'Réponses', value: String(counts.replied) },
      { label: 'Refus', value: String(counts.optedOut) },
      { label: 'Adresses en erreur', value: String(counts.bounced) },
    ],
  }), [counts.contacted, counts.active, counts.replied, counts.optedOut, counts.bounced]);

  const { collection, records } = app.list!;
  const members = useMemo(() => new Set(campaign.recordIds), [campaign.recordIds]);
  const own = useMemo(() => records.filter((record) => members.has(record.id)), [records, members]);
  const titleOf = (recordId: string) => {
    const record = records.find((item) => item.id === recordId);
    return record ? recordTitle(record, collection) : '';
  };
  const personOf = (contactId: string) => data.state?.contacts.find((contact) => contact.id === contactId)?.fullName ?? '';
  // The parameters are the list's columns (hand-added ones flagged `manual`), then the person and the sender.
  const parameters = outreachParameters(collection.columns.map((column) => ({
    key: column.key, label: column.label, manual: Boolean((column as { manual?: boolean }).manual),
  })));
  const prospects = own.filter((record) => !record.archivedAt).flatMap((record) => {
    const contact = data.primaryOf(record.id);
    return contact ? [{
      recordId: record.id, title: recordTitle(record, collection), cells: record.computed,
      person: { fullName: contact.fullName, jobTitle: contact.jobTitle, email: contact.email },
    }] : [];
  });
  const senderName = app.members.find((member) => member.userId === campaign.ownerUserId)?.name ?? '';

  const act = (body: Record<string, unknown>, after?: () => void) => outreachAction(body)
    .then(() => { after?.(); void data.reload(); })
    .catch((cause: unknown) => setNotice(cause instanceof Error ? cause.message : 'Action impossible.'));
  const rename = () => {
    const next = name?.trim();
    setName(null);
    if (next && next !== campaign.name) void act({ action: 'rename_campaign', sequenceId: campaign.id, name: next });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-domino-inkSoft hover:text-domino-aubergine">
          <ChevronLeft className="h-4 w-4" />Campagnes
        </button>
        <span className="text-domino-inkSoft">/</span>
        {name === null
          ? <button type="button" disabled={!data.isOwner} onClick={() => setName(campaign.name)}
              className="rounded-md px-1 text-base font-semibold text-domino-ink enabled:hover:bg-domino-plumTint">{campaign.name}</button>
          : <input autoFocus aria-label="Nom de la campagne" value={name} maxLength={120} onChange={(event) => setName(event.target.value)} onBlur={rename}
              onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); if (event.key === 'Escape') setName(null); }}
              className="w-72 rounded-md border border-domino-cardBorder px-2 py-1 text-base font-semibold text-domino-ink focus:border-domino-aubergine focus:outline-none" />}
        {!data.isOwner && senderName && <span className="text-sm text-domino-inkSoft">· {senderName}</span>}
        {data.isOwner && enrollments.length === 0 && (
          <button type="button" className="ml-1 text-xs text-domino-inkSoft hover:text-domino-aubergine"
            onClick={() => { void act({ action: 'delete_campaign', sequenceId: campaign.id }, () => onOpen(null)); }}>
            Supprimer la campagne
          </button>
        )}
      </div>

      {enrollments.length > 0 && <MetricGridView data={band} hideTitle />}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="tablist" className="flex gap-1 rounded-xl bg-domino-warm p-1">
          {TABS.map((item) => (
            <button key={item.key} type="button" role="tab" aria-selected={tab === item.key} onClick={() => setTab(item.key)}
              className={cn('rounded-lg px-3 py-1.5 text-sm', tab === item.key ? 'bg-domino-white font-semibold text-domino-ink shadow-sm' : 'text-domino-inkSoft hover:text-domino-ink')}>
              {item.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {data.isOwner && <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>Ajouter depuis la liste</Button>}
          {data.isOwner && canManage && <ImportMenu onChoose={setImporting} />}
          {(toReview.length > 0 || writing > 0) && (
            <button type="button" disabled={toReview.length === 0} onClick={() => setReviewing(true)}
              className="rounded-xl bg-domino-aubergine px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60">
              {toReview.length > 0 ? `Relire ${toReview.length}` : ''}{toReview.length > 0 && writing > 0 ? ' · ' : ''}{writing > 0 ? `${writing} en écriture` : ''}
            </button>
          )}
        </div>
      </div>
      {notice && <p role="status" className="text-xs text-domino-inkSoft">{notice}</p>}

      {tab === 'prospects' && (
        <OutreachProspects toolAppId={appId} sequenceId={campaign.id} isOwner={data.isOwner} collection={collection} records={own} data={data}
          manualColumns={parameters.filter((parameter) => parameter.manual)}
          listColumns={collection.columns.slice(1).filter((column) => !(column as { manual?: boolean }).manual)}
          canManage={canManage} onCellSaved={() => { void app.reload(); }} />
      )}
      {tab !== 'prospects' && (
        <OutreachSetup key={campaign.id} toolAppId={appId} sequenceId={campaign.id} isOwner={data.isOwner} view={tab} steps={campaign.steps} settings={campaign.settings}
          parameters={parameters} prospects={prospects} senderName={senderName} canManage={canManage}
          onSaved={() => { void data.reload(); }} onParametersChanged={() => { void app.reload(); }} />
      )}

      {adding && (
        <CampaignAddDrawer sequenceId={campaign.id} collection={collection} records={records} inCampaign={members}
          onClose={() => setAdding(false)} onAdded={() => { void data.reload(); }} />
      )}
      {importing && (
        <TableImportDrawer toolAppId={appId} source={importing} initialCampaign={{ sequenceId: campaign.id }}
          onClose={() => setImporting(null)} onImported={() => { void app.reload(); void data.reload(); }} />
      )}
      {reviewing && (
        <OutreachReview sequenceId={campaign.id} enrollments={toReview} titleOf={titleOf} personOf={personOf}
          onClose={() => setReviewing(false)} onDone={() => { void data.reload(); }} />
      )}
    </div>
  );
};
